(() => {
  // Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
  const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
  const T = (k, f) => D(window.C365_T && window.C365_T[k]) || f;
  const MATERIALS_EN = {
    1: 'Paper', 2: 'Cotton', 3: 'Leather', 4: 'Fruit & Flowers', 5: 'Wood', 6: 'Iron', 7: 'Wool', 8: 'Bronze', 9: 'Pottery',
    10: 'Tin', 11: 'Steel', 12: 'Silk', 13: 'Lace', 14: 'Ivory', 15: 'Crystal', 20: 'China', 25: 'Silver', 30: 'Pearl',
    35: 'Coral', 40: 'Ruby', 45: 'Sapphire', 50: 'Gold', 55: 'Emerald', 60: 'Diamond',
  };
  const MATERIALS = Object.assign({}, MATERIALS_EN, Object.fromEntries(Object.entries(T('collection.materials', {})).map(([y, m]) => [y, D(m)])));
  const MILESTONES = [1, 2, 3, 5, 10, 15, 20, 25, 40, 50];
  const ord = (n) => {
    const s = [T('collection.ord_th', 'th'), T('collection.ord_st', 'st'), T('collection.ord_nd', 'nd'), T('collection.ord_rd', 'rd')];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  class C365Collection extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-grid]');
      this.top();
      this.interleave();
      this.bindSeo();
      this.bindLoadMore();
      if (this.dataset.type === 'wall') {
        this.wallFilters();
        this.inSearch();
      }
      if (this.dataset.type === 'holiday') this.countdown();
      if (this.dataset.type === 'anniversary') this.years();
    }

    word(n) {
      return `${n} ${n === 1 ? this.dataset.word : this.dataset.words}`;
    }

    /* 2+ ticked designs → round-robin by design, keeping the chosen sort inside each. */
    interleave(cards) {
      const order = (this.dataset.interleave || '').split('|').filter(Boolean);
      if (order.length < 2 || !this.grid) return;
      const all = cards || Array.from(this.grid.querySelectorAll('[data-card]:not([data-pin]):not([data-top])'));
      const buckets = order.map((d) => all.filter((c) => c.dataset.design === d));
      const rest = all.filter((c) => !order.includes(c.dataset.design));
      const mixed = [];
      for (let k = 0; buckets.some((b) => b[k]); k++) buckets.forEach((b) => b[k] && mixed.push(b[k]));
      const pin = this.grid.querySelector('[data-pin]');
      [...mixed, ...rest].forEach((c, i) => {
        this.grid.appendChild(c);
        if (i === 0 && pin) this.grid.appendChild(pin);
      });
    }

    /* ?top=<handle> (links from ads and emails): that product first, the rest in the chosen sort. On page 1 a card
       already in the grid moves up; otherwise it comes from templates/product.c365-card.liquid (nothing when the product
       is not in this collection), but not while filters are on: that card can't tell whether it matches them. Later pages
       and Load more leave it out, it is on page 1. Filters, sort and page links keep ?top=. */
    top() {
      const params = new URLSearchParams(location.search);
      const handle = (params.get('top') || '').trim().toLowerCase();
      if (!handle || !this.grid) return;
      this.topHandle = handle;
      const form = this.querySelector('[data-srch-form]');
      if (form && !form.querySelector('input[name="top"]')) {
        const h = document.createElement('input');
        h.type = 'hidden';
        h.name = 'top';
        h.value = handle;
        form.appendChild(h);
      }
      this.querySelectorAll('.c365-srch-pages a').forEach((a) => {
        const u = new URL(a.href, location.href);
        u.searchParams.set('top', handle);
        a.href = u.pathname + u.search;
      });
      const here = Array.from(this.grid.querySelectorAll('[data-card]')).find((c) => c.dataset.handle === handle);
      if (Number(params.get('page')) > 1) {
        here?.remove();
        return;
      }
      if (here) {
        this.placeTop(here);
        return;
      }
      if ([...params.keys()].some((k) => k.startsWith('filter.'))) return;
      const kind = { wall: 'wall', design: 'design' }[this.dataset.type] || 'card';
      this.topLoaded = fetch(`${location.pathname.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?view=c365-card`)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector(`template[data-kind="${kind}"]`)?.content.querySelector('[data-card]');
          if (!card || this.grid.querySelector('[data-top]')) return;
          if (this.dataset.stars !== 'true') card.querySelector('.c365-srch-card__stars')?.remove();
          this.placeTop(document.importNode(card, true));
        })
        .catch(() => {});
    }

    placeTop(card) {
      card.dataset.top = '';
      this.grid.prepend(card);
    }

    bindSeo() {
      const text = this.querySelector('[data-seo-text]');
      const btn = this.querySelector('[data-seo-toggle]');
      if (!text || !btn) return;
      requestAnimationFrame(() => {
        if (text.scrollHeight <= text.clientHeight + 2) btn.hidden = true;
      });
      btn.addEventListener('click', () => {
        const open = text.classList.toggle('is-open');
        btn.textContent = open ? T('collection.read_less', 'Read less') : T('collection.read_more', 'Read more');
      });
    }

    updateProgress(shown, total) {
      const label = this.querySelector('[data-progress-label]');
      const bar = this.querySelector('[data-progress]');
      if (label) label.textContent = T('collection.showing', 'Showing __SHOWN__ of __TOTAL__').replace('__SHOWN__', shown).replace('__TOTAL__', this.word(total));
      if (bar) bar.style.width = `${total ? Math.round((shown / total) * 100) : 100}%`;
    }

    /* Server pages: fetch the next page and append its cards. */
    bindLoadMore() {
      const btn = this.querySelector('[data-loadmore]');
      if (!btn) return;
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        btn.setAttribute('aria-busy', 'true');
        const html = await fetch(btn.href).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const cards = Array.from(doc.querySelectorAll('[data-grid] [data-card]:not([data-pin])')).filter((c) => !this.topHandle || c.dataset.handle !== this.topHandle);
        cards.forEach((c) => this.grid.appendChild(document.adoptNode(c)));
        this.interleave();
        const next = doc.querySelector('[data-loadmore]');
        const label = doc.querySelector('[data-progress-label]');
        if (label) this.querySelector('[data-progress-label]').textContent = label.textContent;
        const bar = doc.querySelector('[data-progress]');
        if (bar) this.querySelector('[data-progress]').style.width = bar.style.width;
        if (next) btn.href = next.href;
        else btn.remove();
        btn.removeAttribute('aria-busy');
      });
    }

    /* Wall art "Search in this collection": the words become title: terms ANDed with the collection's tag rules
       (data-scope), e.g. title:beach AND tag:"line:wall-art" AND tag:"room:bathroom". The search page shows the words
       and the collection again from in / in_label. */
    inSearch() {
      const form = document.querySelector('[data-in-search]');
      if (!form) return;
      const inputs = [...document.querySelectorAll('input[form="c365-in-search"]')];
      const go = (input) => {
        const words = input.value.replace(/["():*]/g, ' ').trim().split(/\s+/).filter(Boolean);
        if (!words.length) return;
        const q = [...words.map((w) => `title:${w}`), form.dataset.scope].join(' AND ');
        const p = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', in: form.dataset.in, in_label: form.dataset.inLabel });
        window.location.href = `${form.action}?${p}`;
      };
      inputs.forEach((input) => input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        go(input);
      }));
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        go(inputs.find((i) => i.value.trim()) || inputs[0]);
      });
    }

    /* Wall art filters: "Show N more" per group, and the sort radios in the mobile sheet (they feed the toolbar select,
       the only sort_by the form submits). */
    wallFilters() {
      // the sticky mobile bar sits under the sticky header group (announcement bar + header; --header-height leaves
      // the announcement bar out)
      const head = document.getElementById('header-group');
      if (head && 'ResizeObserver' in window) {
        new ResizeObserver(() => this.style.setProperty('--c365-sticky-top', `${Math.round(head.getBoundingClientRect().height)}px`)).observe(head);
      }
      this.querySelectorAll('[data-wf-more]').forEach((btn) => {
        const more = btn.textContent;
        btn.addEventListener('click', () => {
          const opts = btn.closest('.c365-wf').querySelectorAll('[data-more]');
          const open = btn.getAttribute('aria-expanded') !== 'true';
          opts.forEach((o) => (o.hidden = !open));
          btn.setAttribute('aria-expanded', String(open));
          btn.textContent = open ? btn.dataset.labelLess : more;
        });
      });
      const form = this.querySelector('[data-srch-form]');
      const select = form?.querySelector('select[name="sort_by"]');
      if (!form || !select) return;
      form.addEventListener('formdata', (e) => e.formData.delete('c365_sort'));
      this.querySelectorAll('[data-wf-sort]').forEach((r) =>
        r.addEventListener('change', () => {
          select.value = r.value;
          // Browse mode has no "Show" button in the sheet: apply right away
          if (!this.querySelector('.c365-srch-sheet__foot')) form.requestSubmit();
        })
      );
    }

    countdown() {
      const el = this.querySelector('[data-days]');
      const iso = this.dataset.deadline;
      if (!el) return;
      if (!iso) {
        el.closest('.c365-col-count__days').hidden = true;
        return;
      }
      const end = new Date(`${iso}T23:59:59`);
      el.textContent = String(Math.max(0, Math.ceil((end - new Date()) / 86400000)));
    }

    /* Anniversary years: `?year=N` filters client-side (designs with no years suit every year).
       The canonical stays the main Anniversary collection. */
    years() {
      const params = new URLSearchParams(location.search);
      const year = Number(params.get('year')) || 0;
      const base = location.pathname;
      const link = (n) => {
        const p = new URLSearchParams(location.search);
        if (n) p.set('year', n);
        else p.delete('year');
        p.delete('page');
        const q = p.toString();
        return base + (q ? `?${q}` : '');
      };

      // milestone tiles
      this.querySelectorAll('.c365-col-years__row [data-year]').forEach((a) => {
        const n = Number(a.dataset.year);
        a.href = link(n);
        a.classList.toggle('is-active', n === year);
        const sup = a.querySelector('[data-ord]');
        if (sup) sup.textContent = ord(n);
      });

      // "More years" panel, grouped by decade
      const more = this.querySelector('[data-more]');
      const toggle = this.querySelector('[data-more-toggle]');
      const groups = [[1, 10], [11, 20], [21, 30], [31, 40], [41, 50], [51, 60]];
      more.innerHTML = groups
        .map(([a, b]) => {
          const items = [];
          for (let n = a; n <= b; n++) {
            if (MILESTONES.includes(n)) continue;
            items.push(
              `<a href="${link(n)}" class="c365-col-year${n === year ? ' is-active' : ''}" title="${MATERIALS[n] || ''}"><span class="c365-col-year__num">${n}<sup>${ord(n)}</sup></span><span class="c365-col-year__sub">${MATERIALS[n] || ''}</span></a>`
            );
          }
          return `<div class="c365-col-more__row"><span>${a}–${b}</span><div>${items.join('')}</div></div>`;
        })
        .join('');
      const inMore = year && !MILESTONES.includes(year);
      const setOpen = (open) => {
        more.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      };
      // Closed on load; a year picked from the panel shows on the toggle like a milestone tile.
      setOpen(false);
      if (inMore) {
        toggle.classList.add('is-active', 'is-picked');
        toggle.setAttribute('aria-label', T('collection.change_year_aria', '__YEAR__ anniversary, change year').replace('__YEAR__', `${year}${ord(year)}`));
        toggle.innerHTML = `<span class="c365-col-year__num">${year}<sup>${ord(year)}</sup></span><span class="c365-col-year__sub">${MATERIALS[year] ? `${MATERIALS[year]} · ` : ''}${T('collection.change', 'Change')} ${toggle.querySelector('svg').outerHTML}</span>`;
      }
      toggle.addEventListener('click', () => setOpen(more.hidden));

      // hero + breadcrumb
      if (year) {
        const name = `${year}${ord(year)}`;
        const mat = MATERIALS[year];
        this.querySelector('[data-hero-title]').textContent = T('collection.year_title', '__YEAR__ anniversary gifts').replace('__YEAR__', name);
        const text = this.querySelector('[data-hero-text]');
        if (text) {
          text.hidden = false;
          text.textContent =
            year === 2
              ? T('collection.year_text_2', 'Cotton is the traditional 2nd anniversary gift, and canvas is cotton. Add your song, vows or a favorite photo.')
              : (year > 1
                  ? T('collection.year_text_other', 'Celebrate __YEAR__ years together with your song, your story or a favorite photo, on a canvas made to keep.')
                  : T('collection.year_text_one', 'Celebrate __YEAR__ year together with your song, your story or a favorite photo, on a canvas made to keep.')
                ).replace('__YEAR__', year);
        }
        const badge = this.querySelector('[data-material]');
        if (badge && mat) {
          badge.hidden = false;
          badge.textContent = T('collection.traditional_gift', 'Traditional gift: __MATERIAL__').replace('__MATERIAL__', mat);
        }
        this.querySelectorAll('[data-year-crumb]').forEach((el) => (el.hidden = false));
        this.querySelector('[data-crumb-current]').textContent = T('collection.year_crumb', '__YEAR__ Anniversary').replace('__YEAR__', name);
        // keep the year when filters / sort submit
        const form = this.querySelector('[data-srch-form]');
        if (form && !form.querySelector('input[name="year"]')) {
          const h = document.createElement('input');
          h.type = 'hidden';
          h.name = 'year';
          h.value = String(year);
          form.appendChild(h);
        }
      }

      // filter + reveal in pages
      const per = Number(this.dataset.perPage) || 24;
      const cards = Array.from(this.grid.querySelectorAll('[data-card]'));
      const matches = cards.filter((c) => {
        if (!year) return true;
        const ys = (c.dataset.years || '').split(',').map((s) => s.trim()).filter(Boolean);
        return !ys.length || ys.includes(String(year));
      });
      cards.forEach((c) => (c.hidden = true));
      let shown = 0;
      const reveal = () => {
        matches.slice(shown, shown + per).forEach((c) => (c.hidden = false));
        shown = Math.min(matches.length, shown + per);
        this.updateProgress(shown, matches.length);
        const btn = this.querySelector('[data-loadmore-local]');
        if (btn) btn.hidden = shown >= matches.length;
      };
      reveal();
      this.querySelector('[data-loadmore-local]')?.addEventListener('click', reveal);
      const count = this.querySelector('[data-count-label]');
      if (count) count.textContent = this.word(matches.length);
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = matches.length > 0;
      const wrap = this.querySelector('[data-loadmore-wrap]');
      if (wrap) wrap.hidden = matches.length === 0;
    }
  }

  if (!customElements.get('c365-collection')) customElements.define('c365-collection', C365Collection);
})();
